import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import styles from "./DashboardFilter.module.scss";
import { CustomCheckbox } from "../controls/StyledCheckbox";
import {
  dashboardDateRangeOptions,
  getDashboardDateRange,
} from "../../containers/dashboard/dateRanges";
import {
  DashboardCountryOption,
  DashboardDateRange,
  DashboardFilters,
} from "../../containers/dashboard/types";

interface DashboardFilterProps {
  filter: DashboardFilters;
  regions: string[];
  countries: DashboardCountryOption[];
  screeningFamilies: string[];
  isApplying: boolean;
  onChange: React.Dispatch<React.SetStateAction<DashboardFilters>>;
  onApply: () => void;
}

const screeningFamilyLabels: Record<string, string> = {
  ALL: "All risk areas and queues",
  PV_PTQ: "PV + PTQ",
  PCO: "PCO",
};

const ALL = "ALL";

const DimensionSelect = ({
  id,
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  allLabel: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const allSelected = value.includes(ALL);
  const checked = allSelected ? [ALL, ...options] : value;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value as string[];
    if (next.includes(ALL) !== allSelected) {
      onChange(allSelected ? [] : [ALL]);
      return;
    }
    const picked = next.filter((option) => option !== ALL);
    onChange(picked.length === options.length ? [ALL] : picked);
  };

  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        multiple
        displayEmpty
        notched
        value={checked}
        label={label}
        onChange={handleChange}
        renderValue={() =>
          allSelected ? allLabel : value.join(", ") || "None selected"
        }
      >
        <MenuItem value={ALL}>
          <CustomCheckbox size="small" checked={allSelected} />
          <ListItemText primary={allLabel} />
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <CustomCheckbox size="small" checked={checked.includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const DashboardFilter = ({
  filter,
  regions,
  countries,
  screeningFamilies,
  isApplying,
  onChange,
  onApply,
}: DashboardFilterProps) => {
  const countriesIn = (selectedRegions: string[]) =>
    Array.from(
      new Set(
        countries
          .filter(
            (country) =>
              selectedRegions.includes(ALL) ||
              selectedRegions.includes(country.region),
          )
          .map((country) => country.name),
      ),
    );
  const familyOptions = screeningFamilies.length ? screeningFamilies : ["ALL"];

  const handleDateRangeChange = (event: SelectChangeEvent) => {
    const dateRange = event.target.value as DashboardDateRange;
    const [startDate, endDate] = getDashboardDateRange(dateRange);
    onChange({ ...filter, dateRange, startDate, endDate });
  };

  const handleRegionsChange = (selectedRegions: string[]) => {
    const availableCountries = countriesIn(selectedRegions);
    const keptCountries = filter.countries.filter((country) =>
      availableCountries.includes(country),
    );
    onChange({
      ...filter,
      regions: selectedRegions,
      countries: keptCountries.length ? keptCountries : [ALL],
    });
  };

  return (
    <Box className={styles.filterWrapper} aria-label="Dashboard filters">
      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-date-range-label">Date range</InputLabel>
        <Select
          labelId="dashboard-date-range-label"
          id="dashboard-date-range"
          value={filter.dateRange}
          label="Date range"
          onChange={handleDateRangeChange}
        >
          {dashboardDateRangeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DimensionSelect
        id="dashboard-region"
        label="Region"
        allLabel="All regions"
        options={regions}
        value={filter.regions}
        onChange={handleRegionsChange}
      />

      <DimensionSelect
        id="dashboard-country"
        label="Country"
        allLabel="All countries"
        options={countriesIn(filter.regions)}
        value={filter.countries}
        onChange={(selectedCountries) =>
          onChange({ ...filter, countries: selectedCountries })
        }
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-screening-family-label">
          Risk area or screening queue
        </InputLabel>
        <Select
          labelId="dashboard-screening-family-label"
          id="dashboard-screening-family"
          value={filter.screeningFamily}
          label="Risk area or screening queue"
          onChange={(event) =>
            onChange({
              ...filter,
              screeningFamily: event.target.value,
              countries: [ALL],
            })
          }
        >
          {familyOptions.map((family) => (
            <MenuItem key={family} value={family}>
              {screeningFamilyLabels[family] ?? family}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        className={styles.applyButton}
        variant="contained"
        onClick={onApply}
        disabled={
          isApplying || !filter.regions.length || !filter.countries.length
        }
      >
        Apply filters
      </Button>
    </Box>
  );
};

export default DashboardFilter;
